import { useCallback, useEffect, useRef, useState } from "react";
import usePolling from "./usePolling";
import { fetchAIReport, getAIErrorMessage } from "../utils/aiInterviewApi";

const RETRY_MS = 10000;
const MAX_WAIT_MS = 3 * 60 * 1000;

// The AI report once `enabled` (the session is COMPLETED). While the API answers REPORT_NOT_READY it retries
// every 10 s for up to 3 minutes. `state`: idle | loading | preparing | timeout | error | ready.
const useAIReport = (interviewId, enabled) => {
  const [report, setReport] = useState(null);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const startedRef = useRef(0);

  const fetchOnce = useCallback(async () => {
    try {
      const next = await fetchAIReport(interviewId);
      if (next) {
        setReport(next);
        setState("ready");
      } else {
        setState(Date.now() - startedRef.current >= MAX_WAIT_MS ? "timeout" : "preparing");
      }
    } catch (err) {
      setError(getAIErrorMessage(err, "Couldn't load the report."));
      setState("error");
    }
  }, [interviewId]);

  const load = useCallback(() => {
    startedRef.current = Date.now();
    setError("");
    setState("loading");
    return fetchOnce();
  }, [fetchOnce]);

  useEffect(() => {
    setReport(null);
    setError("");
    setState("idle");
  }, [interviewId]);

  useEffect(() => {
    if (enabled && state === "idle") load();
  }, [enabled, state, load]);

  usePolling(fetchOnce, RETRY_MS, enabled && state === "preparing");

  return { report, setReport, state, error, reload: load };
};

export default useAIReport;
